import React from 'react';
import { Building2, Shield, Wallet, Users, BookOpen, AlertTriangle, Calendar, History, ArrowRight } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { LEGAL_FORMS, FISCAL_REGIMES, canUpgradeTo } from '../../data/legalForms';

/**
 * StatutJuridiqueTab — viewer + manual upgrade for the legal form.
 * Shows current status, pending change banner if any, history, and
 * upgrade buttons for accessible higher tiers.
 */
export default function StatutJuridiqueTab({ company, monthlyHistory, onUpgrade }) {
  if (!company) return <p className="text-slate-500 text-sm">Aucune société active.</p>;

  const currentForm = LEGAL_FORMS[company.legalForm] || LEGAL_FORMS.ei;
  const currentRegime = FISCAL_REGIMES[company.fiscalRegime] || FISCAL_REGIMES.simplifie;
  const pending = company.pendingLegalFormChange;
  const history = company.legalFormHistory || [];

  // CA glissant 12 mois.
  const ca12mGlissant = (monthlyHistory || []).slice(-12).reduce((s, m) => s + (m.totalRevenue || 0), 0);

  const upgradableForms = Object.values(LEGAL_FORMS).filter(lf =>
    canUpgradeTo(company.legalForm, lf.id)
  );

  return (
    <div className="space-y-4" data-testid="statut-tab">
      {/* Pending change banner */}
      {pending && (
        <div className="bg-warn/10 border border-warn/40 rounded-lg p-4" data-testid="pending-legal-change">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-300">Évolution programmée au 01/01/{pending.plannedYear}</p>
              <p className="text-xs text-slate-300 mt-1">
                Votre statut basculera automatiquement vers <strong>{LEGAL_FORMS[pending.targetForm]?.fullLabel}</strong>{' '}
                au régime <strong>{FISCAL_REGIMES[pending.targetRegime]?.name}</strong>.
              </p>
              <p className="text-[10px] text-slate-500 mt-1">
                Vous pouvez anticiper en cliquant sur le statut cible ci-dessous pour faire la bascule immédiatement.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current status card */}
      <div className="bg-ink-800 rounded-xl border-2 p-5" style={{ borderColor: currentForm.color }} data-testid="current-status">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{currentForm.icon}</span>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Statut actuel</p>
              <h3 className="text-xl font-bold" style={{ color: currentForm.color }}>{currentForm.fullLabel}</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Régime fiscal : <strong>{currentRegime.name}</strong> · Capital social : <strong className="font-mono">{formatMoney(company.capitalSocial || 0)}</strong>
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">CA glissant 12 mois</p>
            <p className="text-lg font-bold font-mono text-slate-200">{formatMoney(ca12mGlissant)}</p>
            <p className="text-[10px] text-slate-500">Seuil régime : <strong>{formatMoney(currentRegime.seuilCA === Infinity ? 999999999 : currentRegime.seuilCA)}</strong></p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] mt-3 pt-3 border-t border-ink-600">
          <Stat icon={Shield} label="Responsabilité" value={currentForm.liabilityLimited ? 'Limitée au capital' : 'Sur patrimoine pro'} good={currentForm.liabilityLimited} />
          <Stat icon={Users} label="Régime dirigeant" value={currentForm.dirigeantRegime === 'tns' ? 'TNS' : 'Assimilé salarié'} />
          <Stat icon={Wallet} label="Cotisations" value={`${Math.round(currentForm.chargesSocialesPct * 100)}%`} />
          <Stat icon={Building2} label="Bonus banque" value={currentForm.banqueRateModifier === 0 ? 'Neutre' : `${currentForm.banqueRateModifier > 0 ? '+' : ''}${currentForm.banqueRateModifier}%`} good={currentForm.banqueRateModifier < 0} />
        </div>

        <details className="mt-3 cursor-pointer group">
          <summary className="text-xs text-purple-300 flex items-center gap-1 list-none">
            <BookOpen className="w-3 h-3" /> Note pédagogique
            <span className="text-slate-500 group-open:hidden ml-1">▾</span>
            <span className="text-slate-500 hidden group-open:inline ml-1">▴</span>
          </summary>
          <p className="text-[10px] text-slate-400 mt-1 italic leading-relaxed pl-4">{currentForm.teachingNote}</p>
          <p className="text-[10px] text-slate-400 mt-1.5 italic leading-relaxed pl-4">
            <strong>Régime {currentRegime.name} :</strong> {currentRegime.teachingNote}
          </p>
        </details>
      </div>

      {/* Upgrade options */}
      {upgradableForms.length > 0 && (
        <div data-testid="upgrade-options">
          <p className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-2">Évolutions possibles</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {upgradableForms.map(lf => {
              const cost = lf.capitalRecommended || lf.capitalMin || 0;
              const isPendingTarget = pending && pending.targetForm === lf.id;
              return (
                <div
                  key={lf.id}
                  className={`rounded-lg border p-3 transition-all ${
                    isPendingTarget ? 'border-amber-400 bg-amber-500/5' : 'border-ink-600 bg-ink-800 hover:border-slate-500'
                  }`}
                  data-testid={`upgrade-to-${lf.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{lf.icon}</span>
                      <div>
                        <p className="font-bold text-sm" style={{ color: lf.color }}>{lf.shortName}</p>
                        <p className="text-[10px] text-slate-500">{lf.fullLabel}</p>
                      </div>
                    </div>
                    {isPendingTarget && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono uppercase">Auto 01/01</span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 leading-snug mb-2">{lf.description}</p>
                  <div className="grid grid-cols-2 gap-1 text-[10px] mb-3">
                    <p className="text-slate-500">Capital : <span className="font-mono text-slate-200">{formatMoney(cost)}</span></p>
                    <p className="text-slate-500">Cotis. : <span className="font-mono text-slate-200">{Math.round(lf.chargesSocialesPct * 100)}%</span></p>
                    <p className="text-slate-500">Resp. : <span className={lf.liabilityLimited ? 'text-emerald-300' : 'text-amber-300'}>{lf.liabilityLimited ? 'Limitée' : 'Sur patrimoine'}</span></p>
                    <p className="text-slate-500">Banque : <span className={lf.banqueRateModifier < 0 ? 'text-money' : 'text-slate-300'}>{lf.banqueRateModifier === 0 ? 'Neutre' : `${lf.banqueRateModifier > 0 ? '+' : ''}${lf.banqueRateModifier}%`}</span></p>
                  </div>
                  <button
                    onClick={() => onUpgrade(lf.id)}
                    className="w-full text-xs px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
                    data-testid={`upgrade-btn-${lf.id}`}
                  >
                    {isPendingTarget ? 'Anticiper le passage' : 'Évoluer vers ce statut'}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-3" data-testid="legal-form-history">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-3.5 h-3.5 text-slate-500" />
            <p className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Historique des statuts</p>
          </div>
          <ol className="space-y-1.5">
            {history.map((h, i) => (
              <li key={`hist-${h.atDay}-${h.to || h.from || i}`} className="text-xs flex items-center gap-2" data-testid={`history-entry-${i}`}>
                <Calendar className="w-3 h-3 text-slate-600" />
                <span className="text-slate-400 font-mono">J{h.atDay}</span>
                <span className="text-slate-500">·</span>
                {h.from && (
                  <>
                    <span className="text-slate-400">{LEGAL_FORMS[h.from]?.shortName || h.from}</span>
                    <ArrowRight className="w-3 h-3 text-slate-600" />
                  </>
                )}
                <span className="font-semibold" style={{ color: LEGAL_FORMS[h.to]?.color || '#E2E8F0' }}>
                  {LEGAL_FORMS[h.to]?.shortName || h.to}
                </span>
                <span className="text-slate-500 italic text-[10px] ml-2">{h.reason}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, good }) {
  return (
    <div className="bg-ink-700/30 rounded-md p-2">
      <div className="flex items-center gap-1 text-slate-500 mb-0.5">
        <Icon className="w-3 h-3" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`font-mono text-xs ${good === true ? 'text-money' : good === false ? 'text-amber-300' : 'text-slate-200'}`}>{value}</p>
    </div>
  );
}
